import React, { useContext } from 'react';
import ColumnChart from '../common/charts/columnChart';
import { UserContext } from '../../context/UserContext';
import Loading from '../common/Loading';
import { fetchIcon } from '../../utils/iconUtils';
import WorldMap from '../common/charts/WorldMap';
import { UnlockIcon } from '../../svg';
import RowChart from '../common/charts/RowChart';
import UpgradeBanner from '../common/UpgradeBackdrop/upgradeBanner';
import { AnalyticsData } from '../../hooks/useAnalytics';
import { getCountryFlag } from '../../utils/countryUtils';
import useI18n from '../../hooks/useI18n';
import { useGeneralAnalyticsQuery } from '../../hooks/queries/useAnalyticsQueries';
import { useQueryClient } from '@tanstack/react-query';
import { useForceRefresh } from '../../hooks/useForceRefresh';
import RefreshButton from '../common/RefreshButton';

interface Props {
  hasSubscription: boolean;
}

const MOCK_ANALYTICS_DATA: AnalyticsData = {
  totalClicks: 2547,
  uniqueClicks: 1823,
  conversionRate: 12.5,
  clicksGrowth: 8.7,
  bestPerformingPlatform: 'Instagram',
  bestPerformingDevice: 'Mobile',
  timeFrameView: [
    { _id: '2023-01', count: 125 },
    { _id: '2023-02', count: 180 },
    { _id: '2023-03', count: 210 },
    { _id: '2023-04', count: 195 },
    { _id: '2023-05', count: 245 },
    { _id: '2023-06', count: 320 },
  ],
  deviceTypeDistribution: {
    mobile: 68,
    tablet: 12,
    desktop: 20,
  },
  countryCounts: [
    { _id: 'US', count: 850 },
    { _id: 'FR', count: 425 },
    { _id: 'GB', count: 375 },
    { _id: 'TN', count: 245 },
    { _id: 'DE', count: 210 },
    { _id: 'JP', count: 180 },
    { _id: 'AU', count: 160 },
    { _id: 'ES', count: 145 },
  ],
  topSources: [
    { _id: 'Instagram', count: 780, percentage: 31 },
    { _id: 'Facebook', count: 645, percentage: 25 },
    { _id: 'Twitter', count: 425, percentage: 17 },
    { _id: 'LinkedIn', count: 350, percentage: 14 },
    { _id: 'TikTok', count: 347, percentage: 13 },
  ],
  lastUpdated: new Date(),
};

const DEFAULT_ANALYTICS_DATA: AnalyticsData = {
  totalClicks: 0,
  uniqueClicks: 0,
  conversionRate: 0,
  clicksGrowth: 0,
  bestPerformingPlatform: '',
  bestPerformingDevice: '',
  timeFrameView: [],
  deviceTypeDistribution: {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
  countryCounts: [],
  topSources: [],
  lastUpdated: undefined,
};

const AnalyticsSection: React.FC<Props> = ({ hasSubscription }) => {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const { t, currentLanguage } = useI18n();

  const { data, isLoading, isError } = useGeneralAnalyticsQuery(
    'year',
    hasSubscription && !!user,
  );

  const queryClient = useQueryClient();
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    () =>
      queryClient.refetchQueries({
        queryKey: ['analytics', 'general'],
        type: 'active',
      }),
    'dashboard-analytics-section',
  );

  const analyticsData: AnalyticsData = !hasSubscription
    ? MOCK_ANALYTICS_DATA
    : (data as AnalyticsData) || DEFAULT_ANALYTICS_DATA;

  if (isError) {
    return <div>{String(t('errors.somethingWentWrong'))}</div>;
  }

  const minClicks = Math.min(
    ...analyticsData.countryCounts.map((c) => c.count),
  );
  const maxClicks = Math.max(
    ...analyticsData.countryCounts.map((c) => c.count),
  );

  return (
    <div className="mt-12 relative">
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-lg font-content">
          {t('common.analytics')}
        </h1>
        {hasSubscription && !!user && (
          <RefreshButton
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            secondsRemaining={secondsRemaining}
          />
        )}
      </div>
      {!hasSubscription && (
        <UpgradeBanner
          containerProps={{
            className:
              '!justify-normal md:!justify-center !backdrop-blur-sm !top-[43px]',
          }}
          headingProps={{
            className: '!mt-52 md:!mt-0',
          }}
          buttonProps={{
            className: '!bg-[white] !text-[#020D3A]',
          }}
          headingText={String(t('upgrade.title'))}
          icon={
            <UnlockIcon
              className="size-4 cursor-pointer select-none"
              stroke="#4D494F"
            />
          }
        />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-4">
        {/* Top Sources */}
        <div className="border border-[#E5E9EB] h-[300px] lg:h-full flex flex-col justify-between items-start px-[20px] py-[20px] rounded-2xl w-full">
          <span className="text-[#4C4C4C] font-content whitespace-nowrap">
            {t('analytics.topSources')}
          </span>
          <div className="flex justify-around w-full h-full gap-4">
            {isLoading ? (
              <Loading />
            ) : analyticsData.topSources.length === 0 ? (
              <div className="flex items-center justify-center h-full w-full">
                <span className="text-gray-500 text-lg font-semibold">
                  {t('common.noData')}
                </span>
              </div>
            ) : (
              analyticsData.topSources.slice(0, 5).map((source) => {
                return (
                  <div
                    key={source._id}
                    className="relative h-full flex-1 max-w-[60px]"
                  >
                    <ColumnChart
                      percent={
                        source.percentage !== undefined
                          ? source.percentage
                          : source.count > 0
                            ? Math.round(
                                (source.count /
                                  analyticsData.topSources.reduce(
                                    (sum, s) => sum + s.count,
                                    0,
                                  )) *
                                  100,
                              )
                            : 0
                      }
                      logo={fetchIcon(source._id.toLowerCase())}
                      altText={source._id}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Users Traffic by Region */}
        <div className="border border-[#E5E9EB] md:col-span-2 md:flex rounded-2xl p-6 lg:max-h-[550px]">
          <div className="relative flex-1 ">
            <span className="text-[#4C4C4C] font-content whitespace-nowrap">
              {t('analytics.usersTrafficByRegion')}
            </span>
            <div className="flex flex-col justify-around md:h-full mt-[15px] md:mt-[5px]">
              {isLoading ? (
                <Loading />
              ) : (
                <div className={`overflow-hidden aspect-[1.4] md:aspect-[1.5]`}>
                  <WorldMap
                    countries={analyticsData.countryCounts.map((c) => ({
                      country: c._id,
                      clicks: c.count,
                    }))}
                    isDashboardPage
                  />
                </div>
              )}
            </div>
          </div>

          {/* Top Countries */}
          <div className="flex flex-col w-full flex-1 mt-4 lg:mt-0">
            <span className="text-[#4C4C4C] font-content whitespace-nowrap mb-4">
              {t('analytics.topCountries')}
            </span>
            <div className="flex flex-col space-y-4 h-full max-h-[400px] md:max-h-[300px]">
              {isLoading ? (
                <Loading />
              ) : analyticsData.countryCounts.length === 0 ? (
                <div className="flex items-center justify-center h-full w-full">
                  <span className="text-gray-500 text-lg font-semibold">
                    {t('common.noData')}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col space-y-4 overflow-y-auto overflow-x-hidden h-full">
                  {analyticsData.countryCounts
                    .filter((c) => c._id)
                    .sort((a, b) => {
                      if (b.count !== a.count) return b.count - a.count;
                      return a._id.localeCompare(b._id);
                    })
                    .map((c) => {
                      const regionNames = new Intl.DisplayNames(
                        [currentLanguage],
                        {
                          type: 'region',
                        },
                      );

                      const englishRegionNames = new Intl.DisplayNames(['en'], {
                        type: 'region',
                      });

                      let name, englishName;
                      try {
                        if (
                          c._id &&
                          typeof c._id === 'string' &&
                          c._id.length === 2
                        ) {
                          name = regionNames.of(c._id);
                          englishName = englishRegionNames.of(c._id);
                        } else {
                          name = 'Unknown';
                          englishName = 'Unknown';
                        }
                      } catch (error) {
                        console.error('Error getting region name', error);
                        name = c._id || 'Unknown';
                        englishName = c._id || 'Unknown';
                      }

                      const flag = getCountryFlag(englishName ?? '');
                      return {
                        clicks: c.count,
                        country: name,
                        flag,
                      };
                    })
                    .map((countryData) => (
                      <RowChart
                        key={countryData.country}
                        data={{
                          country: countryData.country ?? 'unknown',
                          clicks: countryData.clicks,
                        }}
                        flag={countryData.flag}
                        minClicks={minClicks}
                        maxClicks={maxClicks}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSection;
